import React, { useContext } from 'react';
import { Card, Form, InputNumber, Select } from 'antd';
import { FormOutlined } from '@ant-design/icons';
import { FormFieldPath } from './index';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

export default (formPlugin, namespace) => {

    return {
        Renderer: (props) => {

            const formFieldPath = useContext(FormFieldPath)

            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id)
            }
            const { data } = props
            const liveRender = (props.isPreviewMode || formPlugin.live)

            if (formPlugin.formData.type !== "COMPLEX") {
                if (props.remove) {
                    props.remove()
                }
                return null
            }

            if (!data.productId) {
                if (formPlugin.live) {
                    return null
                }
                return (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        color: "red"
                    }}>
                        <Trans>Veuillez sélectionner un produit.</Trans>
                    </div>
                )
            }

            const product = formPlugin.formData.products.find(i => i.id === data.productId)

            if (!product) {
                if (formPlugin.live) {
                    return null
                }
                return (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        color: "red"
                    }}>
                        <Trans>Produit introuvable (supprimé ?). Sélectionnez un autre produit.</Trans>
                    </div>
                )
            }

            if (!liveRender) {
                return (
                    <div>
                        <div style={{ border: "1px dashed orange", borderRadius: 10, padding: "2%" }}>
                            <Card title={t`Information Complémentaire (${product.label})`} type="inner">
                                {props.children}
                            </Card>
                        </div>
                    </div>
                )
            }

            let afterProduct = data?.afterProduct || 1

            // formRef n'existe que sur le plugin RACINE : dans un bloc de formulaire
            // (plugin interne), on remonte à la racine, sinon la quantité vaut 0 et
            // les occurrences ne sont jamais rendues.
            const quantity = (formPlugin?.rootFormPlugin || formPlugin)?.formRef?.getFieldValue(product.id + "_quantity") || 0

            if (quantity < afterProduct) {
                return false
            }

            return (
                <div>
                    <Form.List name={data.productId} preserve={true}>
                        {() => {
                            return (
                                <div>
                                    {[...Array(quantity - (afterProduct - 1)).keys()].map((index) => {
                                        // DEUX chemins, rôles disjoints :
                                        //  - path (pour buildName/name Antd) : on pousse SEULEMENT l'index.
                                        //    Le Form.List name={productId} préfixe lui-même par productId →
                                        //    name final = productId[index][...sub]. Donc PAS de productId ici.
                                        //  - absolutePath (pour naviguer dans formValues : conditions/scope) :
                                        //    on pousse productId PUIS index → formValues[productId][index].
                                        // listFieldProps ne porte plus d'identité (inerte, compat spread).
                                        const parentAbs = Array.isArray(formFieldPath.absolutePath) ? formFieldPath.absolutePath : (formFieldPath.path || [])
                                        // storePath (store du Form racine) : même segment productId+index.
                                        // Diffère d'absolutePath uniquement quand un parent est un bloc de
                                        // formulaire (cf. useFormPath).
                                        const parentStore = Array.isArray(formFieldPath.storePath) ? formFieldPath.storePath : parentAbs
                                        const childContext = {
                                            path: [...formFieldPath.path, index],
                                            absolutePath: [...parentAbs, data.productId, index],
                                            storePath: [...parentStore, data.productId, index],
                                            listFieldProps: null,
                                        }

                                        // ⚠️ key GLOBALEMENT unique : préfixée par nodeId + productId.
                                        // Sinon plusieurs occurrences (ou plusieurs productInfo) partagent les
                                        // keys 0,1,2… → React « Encountered two children with the same key »
                                        // → réconciliation cassée → démontage/remontage du sous-arbre (et donc
                                        // des form-blocks qu'il contient, qui perdent alors leurs valeurs).
                                        const reactKey = props.nodeId + "_" + data.productId + "_" + index

                                        return (
                                            <FormFieldPath.Provider key={reactKey} value={childContext}>
                                                <Card
                                                    title={t`${product.title} #${index + 1 + (afterProduct - 1)}`}
                                                    type="inner"
                                                    style={{ marginBottom: 20 }}
                                                >
                                                    {/* Les enfants sont les MÊMES props.children pour chaque
                                                        occurrence → on doit re-keyer par occurrence, sinon
                                                        l'occurrence 0 et 1 ont des enfants à keys identiques
                                                        (collision → remontage). */}
                                                    {React.Children.map(props.children, (child, childIdx) =>
                                                        React.isValidElement(child)
                                                            ? React.cloneElement(child, { key: reactKey + "_c" + childIdx })
                                                            : child
                                                    )}
                                                </Card>
                                            </FormFieldPath.Provider>
                                        )
                                    })}
                                </div>
                            )
                        }}
                    </Form.List>
                </div>
            )
        },
        namespace: namespace,
        id: 'form-product-info' + namespace,
        title: t`Information Complémentaire`,
        description: t`Élément de formulaire utilisable.`,
        icon: <FormOutlined />,
        iconColor: "#8E44AD",
        version: 1,
        hideInMenu: formPlugin.formData.type !== "COMPLEX",
        allowClickInside: false,
        ignoreLayoutParams: true,
        cellPlugins: (plugins, data) => {
            if (!data.productId) {
                return []
            }
            const newNamespace = namespace + data.productId + "/"
            formPlugin.registerNamespace(newNamespace, data.productId)

            return formPlugin.getPluginsMem(namespace)
                .filter(i =>
                    !i.id.includes("form-identity") &&
                    !i.id.includes("form-product") &&
                    !i.id.includes("form-step-container") &&
                    !i.id.includes("form-submit-button") &&
                    !i.id.includes("form-free-amount") &&
                    !i.id.includes("form-paiement-method") &&
                    !i.id.includes("form-calendar")
                )
                .map(i => {
                    if (i.id.includes("form")) {
                        // Cloner pour ne pas muter les plugins partagés (sinon leur id se concatène à l'infini)
                        return { ...i, namespace: newNamespace, id: i.id + data.productId + "/" }
                    }
                    return i
                })
        },
        controls: {
            type: 'custom',
            Component: (props) => {
                const productId = props.data?.productId || null
                const afterProduct = props.data?.afterProduct || 1
                const products = formPlugin.formData.products

                return (
                    <div>
                        <Form.Item label={t`Produit`}>
                            <Select
                                placeholder={t`Choisir un produit`}
                                value={productId}
                                onChange={(v) => props.onChange({ productId: v })}
                            >
                                {products.map(i => (
                                    <Select.Option value={i.id}>
                                        {i.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item label={t`Demander à partir du`}>
                            <InputNumber
                                decimalSeparator={','}
                                min={1}
                                step={1}
                                addonAfter={t`Produit(s)`}
                                value={afterProduct}
                                onChange={(v) => props.onChange({ afterProduct: v })}
                            />
                        </Form.Item>
                    </div>
                )
            }
        },
    }
}
